import { useMemo, useState } from "react";
import { TriangleAlert } from "lucide-react";

import { AttendanceApprovalQueue, type AttendanceApprovalRequest } from "./AttendanceApprovalQueue.js";
import { ShiftDayRequestQueue, type ShiftDayRequestRow } from "./ShiftDayRequestQueue.js";
import { WorkScheduleCard } from "./WorkScheduleCard.js";
import { PendingAttendanceCorrectionsQueue, type CorrectionRequest } from "./PendingAttendanceCorrectionsQueue.js";
import { WeeklyHoursCorrectionQueue, type WeeklyHoursCorrectionRow } from "./WeeklyHoursCorrectionQueue.js";
import { WeeklyBiometricAttendance } from "./WeeklyBiometricAttendance.js";
import { MemberAttendanceHistory } from "./MemberAttendanceHistory.js";
import {
  avatarColor, approverRoleLabel, MultiListCard,
  type TeamMemberDetail, type MemberHistoryItem,
} from "../pages/AttendancePage.js";
import { SpecularButton } from "./ui/specular-button.js";
import { cn } from "../lib/utils.js";
import { Tooltip } from "./ui/tooltip.js";
import { DropdownSelect } from "./DropdownButton.js";

/** One template shared by the header and every row, so the columns stay aligned. */
const HISTORY_GRID = "grid grid-cols-[minmax(0,1.4fr)_132px_minmax(0,1fr)_104px] items-center gap-2";

type HistoryStatus = "ALL" | "APPROVED" | "REJECTED";

// "ALL" is this filter's own all-value, so the dropdown is not clearable -- a "" row
// would be a second, invalid way to say the same thing.
const HISTORY_STATUS_OPTIONS = [
  { value: "ALL", label: "All statuses" },
  { value: "APPROVED", label: "Approved" },
  { value: "REJECTED", label: "Declined" },
];

interface Props {
  memberDetail: TeamMemberDetail;
  memberWeeklyCorrections: WeeklyHoursCorrectionRow[];
  memberCorrections: CorrectionRequest[];
  memberOvertime: AttendanceApprovalRequest[];
  memberShiftDayRequests: ShiftDayRequestRow[];
  memberHistory: MemberHistoryItem[];
  onRefresh: () => void;
  onBack: () => void;
  backLabel: string;
}

export function TeamMemberDetailPanel({
  memberDetail, memberWeeklyCorrections, memberCorrections, memberOvertime, memberShiftDayRequests, memberHistory,
  onRefresh, onBack, backLabel,
}: Props) {
  const [historyStatus, setHistoryStatus] = useState<HistoryStatus>("ALL");
  // One "which dropdown is open" state for the panel: opening one closes any other.
  const [openFilter, setOpenFilter] = useState<string | null>(null);
  const visibleHistory = useMemo(
    () => (historyStatus === "ALL" ? memberHistory : memberHistory.filter((h) => h.status === historyStatus)),
    [memberHistory, historyStatus],
  );

  return (
    // Breathing room under the last section so the panel never ends flush
    // against the bottom edge of its scroll area.
    <div className="pb-8">
      {/* Back and the identity share one row -- the button is a drill-down "back to the
          list", so it reads as belonging to this person's header rather than sitting
          above it. It wraps onto its own line when the name runs long. */}
      <div className="mb-4 flex flex-wrap items-center gap-4">
        <SpecularButton onClick={onBack}>
          &larr; {backLabel}
        </SpecularButton>

        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 44, height: 44, borderRadius: "50%", background: avatarColor(memberDetail.member.id), color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, fontWeight: 700, flexShrink: 0 }}>
            {memberDetail.member.firstName[0]}{memberDetail.member.lastName[0]}
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16 }}>{memberDetail.member.firstName} {memberDetail.member.lastName}</div>
            <div style={{ fontSize: 12, color: "var(--color-surface-700)" }}>
              {memberDetail.member.designation} &middot; {memberDetail.member.employeeCode}
              {memberDetail.member.managerName && <> &middot; Reports to {memberDetail.member.managerName}</>}
            </div>
          </div>
        </div>
      </div>

      {/* Nobody can check in without working hours, so when this person has none it
          is the first thing their manager should see -- and the editor to fix it is
          the profile's own Work Schedule section, not a second copy of it. */}
      {memberDetail.member.shift && !memberDetail.member.shift.hasShift && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-[var(--radius-lg)] border border-[var(--color-warning-border)] bg-[var(--color-warning-bg)] px-4 py-2.5">
          <TriangleAlert size={16} className="shrink-0 text-[var(--color-warning-fg)]" />
          <span className="text-[13px] font-semibold">No shift timing set &mdash; they cannot check in.</span>
          {memberDetail.member.shift.nudgeSentAt && (
            <span className="text-[12px] text-surface-600">
              Asked on {new Date(memberDetail.member.shift.nudgeSentAt).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
            </span>
          )}
          <span className="text-[12px] text-surface-600">Set it in Work Schedule below.</span>
        </div>
      )}

      <div className="mb-4 w-full">
        <WorkScheduleCard employeeId={memberDetail.member.id} onSaved={onRefresh} />
      </div>

      <MultiListCard
        sections={[
          ...(memberWeeklyCorrections.length > 0 ? [{
            key: "weekly-corrections",
            title: "Weekly Hours Correction",
            count: memberWeeklyCorrections.length,
            content: <WeeklyHoursCorrectionQueue requests={memberWeeklyCorrections} onRefresh={onRefresh} />,
          }] : []),
          ...(memberOvertime.length > 0 ? [{
            key: "attendance-approvals",
            title: "Attendance Approval",
            count: memberOvertime.length,
            content: <AttendanceApprovalQueue requests={memberOvertime} onRefresh={onRefresh} />,
          }] : []),
          ...(memberShiftDayRequests.length > 0 ? [{
            key: "shift-day-requests",
            title: "Shift Time Change",
            count: memberShiftDayRequests.length,
            content: <ShiftDayRequestQueue requests={memberShiftDayRequests} onRefresh={onRefresh} />,
          }] : []),
          ...(memberCorrections.length > 0 ? [{
            key: "corrections",
            title: "Insufficient hours",
            count: memberCorrections.length,
            content: <PendingAttendanceCorrectionsQueue requests={memberCorrections} onRefresh={onRefresh} />,
          }] : []),
        ]}
      />

      <div style={{ fontSize: 13, fontWeight: 700, color: "var(--color-surface-700)", margin: "var(--space-6) 0 var(--space-2)" }}>
        Weekly Biometric
      </div>
      {/* Full width, like every other section here, so they share one left and right
          edge and the panel uses whatever width the screen gives it. */}
      <div className="w-full">
        <WeeklyBiometricAttendance
          employee={{
            id: memberDetail.member.id,
            firstName: memberDetail.member.firstName,
            lastName: memberDetail.member.lastName,
            employeeCode: memberDetail.member.employeeCode,
            designation: memberDetail.member.designation,
          }}
        />
      </div>

      {memberHistory.length > 0 && (
        <>
          <div className="mb-2 mt-6 flex w-full flex-wrap items-center gap-3">
            <div className="mr-auto" style={{ fontSize: 13, fontWeight: 700, color: "var(--color-surface-700)" }}>
              Recent Approval History
            </div>
            <DropdownSelect
              value={historyStatus}
              onChange={(v) => setHistoryStatus(v as HistoryStatus)}
              placeholder="All statuses"
              options={HISTORY_STATUS_OPTIONS}
              clearable={false}
              ariaLabel="Filter approval history by status"
              open={openFilter === "historyStatus"}
              onToggle={() => setOpenFilter((f) => (f === "historyStatus" ? null : "historyStatus"))}
              onClose={() => setOpenFilter(null)}
            />
          </div>
          {/* One compact row per entry on a shared 4-column grid, so Request,
              Approver, Remark and Status line up under the header on every row. */}
          <div className="w-full overflow-hidden rounded-[var(--radius-lg)] border border-[var(--color-surface-200)] bg-[var(--color-surface-0)]">
            <div className={cn(HISTORY_GRID, "border-b border-[var(--color-surface-200)] bg-[var(--color-surface-50)] px-4 py-2 text-[10px] font-bold uppercase tracking-wide text-[var(--color-surface-600)]")}>
              <span>Request</span>
              <span className="border-l border-[var(--color-surface-200)] pl-3">Approver</span>
              <span className="border-l border-[var(--color-surface-200)] pl-3">Remark</span>
              <span className="border-l border-[var(--color-surface-200)] pl-3 text-right">Status</span>
            </div>
            <div className="min-h-[180px] max-h-[480px] divide-y divide-[var(--color-surface-100)] overflow-y-auto">
              {visibleHistory.length === 0 && (
                <div className="px-4 py-6 text-center text-[12px] text-[var(--color-surface-500)]">
                  No {historyStatus === "APPROVED" ? "approved" : "declined"} entries in this history.
                </div>
              )}
              {visibleHistory.map((item) => {
                const approved = item.status === "APPROVED";
                return (
                  <div key={item.key} className={cn(HISTORY_GRID, "px-4 py-1.5")}>
                    <span className="truncate text-[13px] font-semibold">{item.label}</span>
                    <span className="truncate border-l border-[var(--color-surface-100)] pl-3 text-[11px] text-[var(--color-surface-700)]">
                      {item.approverRole ? approverRoleLabel(item.approverRole) : "—"}
                    </span>
                    {/* Long remarks are cut with an ellipsis; hovering shows the full text. */}
                    <Tooltip content={item.remark}>
                      <span
                        tabIndex={item.remark ? 0 : undefined}
                        className={cn(
                          "truncate border-l border-[var(--color-surface-100)] pl-3 text-[11px] italic text-[var(--color-surface-700)] outline-none",
                          item.remark && "cursor-help focus-visible:ring-2 focus-visible:ring-brand-500",
                        )}
                      >
                        {item.remark ? `"${item.remark}"` : "—"}
                      </span>
                    </Tooltip>
                    <span className="flex justify-end border-l border-[var(--color-surface-100)] pl-3">
                      <span
                        className={cn(
                          "rounded-[var(--radius-full)] px-2.5 py-0.5 text-[11px] font-semibold",
                          approved
                            ? "bg-[var(--color-success-bg)] text-[var(--color-success-fg)]"
                            : "bg-[var(--color-error-bg)] text-[var(--color-error-fg)]",
                        )}
                      >
                        {approved ? "Approved" : "Declined"}
                      </span>
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
      {memberHistory.length === 0 && (
        <div style={{ fontSize: 13, color: "var(--color-surface-700)", marginTop: "var(--space-4)" }}>No approval history yet.</div>
      )}

      {/* The same List / Register views the employee sees under My History, read-only.
          Last in the panel because it is the deepest drill-down: the queues above are
          what the approver has to act on, this is the record behind them. */}
      <div className="mb-2 mt-6 text-[13px] font-bold text-surface-700">
        Attendance History
      </div>
      {/* The register is ~1050px of day columns at its narrowest, so it takes the
          full width available and scrolls inside its own box only when the screen
          is narrower than that. */}
      <div className="w-full">
        <MemberAttendanceHistory employeeId={memberDetail.member.id} />
      </div>
    </div>
  );
}
